import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, Save, GripVertical } from "lucide-react";
import api from "@/lib/api";

export const DEFAULT_TESTIMONIAL_FILTERS = [
  "Youth Athletes", "Volleyball Athletes", "College Athletes",
  "Adult Fitness Clients", "Team Results", "Transformation Stories",
];

export const DEFAULT_PACKAGE_FILTERS = [
  "Performance", "Fitness", "Camps / Clinics",
  "Drop-In Options", "Mavs Volleyball", "Online Coaching",
];

export const DEFAULT_MAVS_STEPS = [
  { id: "step-1", title: "Download the Trilogy App", text: "Get the app on your phone to manage everything in one place.", icon: "Download", button_enabled: true, button_label: "Get the App", button_url: "#mavs-app" },
  { id: "step-2", title: "Create or Login to Your Account", text: "Set up your athlete's profile or sign in to your existing account.", icon: "UserCheck", button_enabled: true, button_label: "Login / Create Account", button_url: "/login" },
  { id: "step-3", title: "Join the Correct Mavs Group", text: "Select your athlete's age group / program inside the app.", icon: "ListChecks", button_enabled: true, button_label: "Open App / Portal", button_url: "https://train.trilogyfitpt.com" },
  { id: "step-4", title: "View the Schedule", text: "Check practice times, tournaments, and events.", icon: "CalendarDays", button_enabled: true, button_label: "View Schedule", button_url: "#mavs-schedule" },
  { id: "step-5", title: "Book or Purchase a Package", text: "Reserve a tryout or pick the Mavs membership that fits.", icon: "CreditCard", button_enabled: true, button_label: "View Packages", button_url: "#mavs-packages" },
  { id: "step-6", title: "Contact Support if Stuck", text: "Our team is here to help parents every step of the way.", icon: "LifeBuoy", button_enabled: true, button_label: "Contact Support", button_url: "/contact" },
];

const uid = () => `step-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const Card = ({ title, desc, children }) => (
  <div className="mt-6 rounded-xl border border-border bg-card p-5" data-testid="inline-manager">
    <h2 className="font-condensed text-lg font-semibold uppercase tracking-wide">{title}</h2>
    {desc && <p className="mt-1 text-sm text-muted-foreground">{desc}</p>}
    <div className="mt-4 space-y-2">{children}</div>
  </div>
);

const SaveBar = ({ onSave, saving, onAdd, addLabel }) => (
  <div className="mt-4 flex items-center gap-3">
    <button onClick={onAdd} data-testid="inline-add" className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-xs font-semibold uppercase hover:bg-accent"><Plus className="h-3.5 w-3.5" /> {addLabel}</button>
    <button onClick={onSave} disabled={saving} data-testid="inline-save" className="inline-flex items-center gap-1.5 rounded-md bg-brand px-4 py-2 text-xs font-semibold uppercase text-white hover:bg-[hsl(var(--brand-red-deep))] disabled:opacity-60"><Save className="h-3.5 w-3.5" /> {saving ? "Saving..." : "Save"}</button>
  </div>
);

// Generic reusable filter-category manager (add / edit / delete) backed by a settings key.
const FiltersPanel = ({ settingKey, defaults, title, desc, addLabel = "Add filter", itemNoun = "page" }) => {
  const [list, setList] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/public/settings").then((r) => {
      const v = r.data?.[settingKey];
      setList(Array.isArray(v) && v.length ? v : defaults);
    }).catch(() => setList(defaults));
  }, [settingKey, defaults]);

  const save = async () => {
    setSaving(true);
    try {
      const clean = (list || []).map((s) => s.trim()).filter(Boolean);
      await api.put("/admin/settings", { [settingKey]: clean });
      setList(clean);
      toast.success(`Filters saved. Public ${itemNoun} updates on next load.`);
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  if (!list) return null;
  return (
    <Card title={title} desc={desc}>
      {list.map((val, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className="flex items-center gap-2" data-testid={`filter-row-${i}`}>
          <GripVertical className="h-4 w-4 text-muted-foreground" />
          <Input value={val} onChange={(e) => setList(list.map((x, xi) => (xi === i ? e.target.value : x)))} data-testid={`filter-input-${i}`} />
          <button onClick={() => setList(list.filter((_, xi) => xi !== i))} data-testid={`filter-remove-${i}`} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border text-destructive hover:bg-destructive/10"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <SaveBar onSave={save} saving={saving} onAdd={() => setList([...list, "New Filter"])} addLabel={addLabel} />
    </Card>
  );
};

// Manage testimonial filter categories (add / edit / delete).
export const ManageFiltersPanel = () => (
  <FiltersPanel
    settingKey="testimonial_filters"
    defaults={DEFAULT_TESTIMONIAL_FILTERS}
    title="Manage Filters"
    desc="These categories power the filter buttons on the public Testimonials page and the Category dropdown when editing a testimonial."
    itemNoun="Testimonials page"
  />
);

// Manage package filter categories (add / edit / delete).
export const ManagePackageFiltersPanel = () => (
  <FiltersPanel
    settingKey="package_filters"
    defaults={DEFAULT_PACKAGE_FILTERS}
    title="Manage Filters"
    desc="These categories power the filter buttons on the public Packages page and the Category dropdown when editing a package."
    itemNoun="Packages page"
  />
);

// Normalize legacy step shapes into the current model (title/text + CTA toggle/label/url).
const normalizeStep = (s) => ({
  id: s.id || uid(),
  title: s.title || "",
  text: s.text || "",
  icon: s.icon || "ListChecks",
  button_enabled: s.button_enabled !== undefined ? !!s.button_enabled : (s.action ? s.action !== "none" : true),
  button_label: s.button_label || "",
  button_url: s.button_url || "",
});

// Manage the Mavs "Get Started in 6 Steps" (title/text + per-step CTA: toggle + label + link).
export const ManageStepsPanel = () => {
  const [list, setList] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/public/settings").then((r) => {
      const v = r.data?.mavs_steps;
      setList(Array.isArray(v) && v.length ? v.map(normalizeStep) : DEFAULT_MAVS_STEPS.map(normalizeStep));
    }).catch(() => setList(DEFAULT_MAVS_STEPS.map(normalizeStep)));
  }, []);

  const upd = (id, key, value) => setList((prev) => prev.map((s) => (s.id === id ? { ...s, [key]: value } : s)));

  const save = async () => {
    setSaving(true);
    try {
      const clean = (list || []).filter((s) => (s.title || "").trim());
      await api.put("/admin/settings", { mavs_steps: clean });
      setList(clean);
      toast.success("Steps saved. Mavs page updates on next load.");
    } catch (e) { toast.error("Save failed"); } finally { setSaving(false); }
  };

  if (!list) return null;
  return (
    <Card title={`Manage "Get Started" Steps`} desc="Edit each step's title and text. Toggle the action button on/off and set its label and link. Steps show on the Mavs Volleyball page.">
      {list.map((s, i) => (
        <div key={s.id} className="rounded-lg border border-border p-3" data-testid={`step-row-${i}`}>
          <div className="flex items-center justify-between">
            <span className="font-condensed text-sm font-semibold uppercase">Step {i + 1}</span>
            <button onClick={() => setList(list.filter((x) => x.id !== s.id))} data-testid={`step-remove-${i}`} className="text-xs font-semibold uppercase text-destructive hover:underline">Remove</button>
          </div>
          <Input className="mt-2" placeholder="Title" value={s.title || ""} onChange={(e) => upd(s.id, "title", e.target.value)} data-testid={`step-title-${i}`} />
          <Input className="mt-2" placeholder="Text" value={s.text || ""} onChange={(e) => upd(s.id, "text", e.target.value)} data-testid={`step-text-${i}`} />
          <Input className="mt-2" placeholder="Icon name (lucide, e.g. Download)" value={s.icon || ""} onChange={(e) => upd(s.id, "icon", e.target.value)} data-testid={`step-icon-${i}`} />
          <div className="mt-3 flex items-center gap-2 rounded-md border border-border bg-muted/30 px-3 py-2">
            <Switch checked={!!s.button_enabled} onCheckedChange={(v) => upd(s.id, "button_enabled", v)} data-testid={`step-button-toggle-${i}`} />
            <span className="text-sm text-muted-foreground">{s.button_enabled ? "Action button shown" : "No action button"}</span>
          </div>
          {s.button_enabled && (
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <Input placeholder="Button label" value={s.button_label || ""} onChange={(e) => upd(s.id, "button_label", e.target.value)} data-testid={`step-button-label-${i}`} />
              <Input placeholder="Button link (URL, /path, or #anchor)" value={s.button_url || ""} onChange={(e) => upd(s.id, "button_url", e.target.value)} data-testid={`step-button-url-${i}`} />
            </div>
          )}
        </div>
      ))}
      <SaveBar onSave={save} saving={saving} onAdd={() => setList([...list, { id: uid(), title: "New Step", text: "", icon: "ListChecks", button_enabled: false, button_label: "", button_url: "" }])} addLabel="Add step" />
    </Card>
  );
};
